import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';

// Recuperação de senha em dois passos:
//  1) 'email'  — usuário informa o e-mail; backend envia um código (OTP) por e-mail
//  2) 'codigo' — usuário digita o código recebido + a nova senha
// Usamos código (não link) porque o app roda dentro de um WebView (APK), onde
// links de e-mail não abrem de forma confiável.
export default function RecuperarSenha() {
  const [etapa, setEtapa] = useState('email');
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [verSenha, setVerSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const solicitar = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/recuperar-senha', { email });
      toast.success('Se houver conta com esse e-mail, o código foi enviado.');
      setEtapa('codigo');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Não foi possível enviar o código.');
    } finally {
      setLoading(false);
    }
  };

  const redefinir = async e => {
    e.preventDefault();
    if (novaSenha !== confirmar) {
      toast.error('As senhas não coincidem.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/redefinir-senha', { email, codigo, novaSenha });
      toast.success('Senha redefinida! Faça login com a nova senha.');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Código inválido ou expirado.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/logo-torqueapp.png" alt="TorqueApp — Gestão para Oficinas" className="w-52 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            {etapa === 'email' ? 'Recuperar senha' : 'Digite o código enviado ao seu e-mail'}
          </p>
        </div>

        {etapa === 'email' ? (
          <form onSubmit={solicitar} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail da conta</label>
              <input
                type="email"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="seu@email.com"
                autoComplete="email"
              />
              <p className="text-xs text-gray-400 mt-1">Enviaremos um código de 6 dígitos válido por 10 minutos.</p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold text-base hover:bg-blue-700 transition-colors disabled:opacity-50 mt-2"
            >
              {loading ? 'Enviando...' : 'Enviar código'}
            </button>
          </form>
        ) : (
          <form onSubmit={redefinir} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código de 6 dígitos</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base tracking-[0.4em] text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={codigo}
                onChange={e => setCodigo(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                autoComplete="one-time-code"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nova senha</label>
              <div className="relative">
                <input
                  type={verSenha ? 'text' : 'password'}
                  required
                  minLength={6}
                  className="w-full border border-gray-300 rounded-lg px-3 py-3 pr-10 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={novaSenha}
                  onChange={e => setNovaSenha(e.target.value)}
                  placeholder="mínimo 6 caracteres"
                  autoComplete="new-password"
                />
                <button type="button" tabIndex={-1}
                  onClick={() => setVerSenha(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-lg px-1">
                  {verSenha ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar nova senha</label>
              <input
                type={verSenha ? 'text' : 'password'}
                required
                minLength={6}
                className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={confirmar}
                onChange={e => setConfirmar(e.target.value)}
                placeholder="repita a nova senha"
                autoComplete="new-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold text-base hover:bg-blue-700 transition-colors disabled:opacity-50 mt-2"
            >
              {loading ? 'Redefinindo...' : 'Redefinir senha'}
            </button>
            <button
              type="button"
              onClick={() => setEtapa('email')}
              className="w-full text-blue-600 text-sm hover:underline"
            >
              Não recebi o código — reenviar
            </button>
          </form>
        )}

        <button
          type="button"
          onClick={() => navigate('/login')}
          className="w-full text-gray-500 text-sm hover:underline mt-4"
        >
          ← Voltar para o login
        </button>
      </div>
    </div>
  );
}
